import React from "react"
import { Link } from "gatsby"
import { IntegrationData } from "./types"
import { btn, btnPrimary } from "../../ds/button.module.css"
import { gradientLightPink } from "../../ds/gradients.module.css"
import PremagicIcon from "../../images/logo/144x144.png"
import FlywheelImage from "../../images/integrations/flywheel.jpg"

interface IntegrationDetailProps {
  data: IntegrationData
}

const IntegrationBreadcrumb = ({ platformName }: { platformName: string }) => (
  <div className="pm-container py-4">
    <p className="text-sm text-gray-500">
      <Link
        to="/integrations/"
        className="hover:text-blue-600 transition-colors"
      >
        Integrations
      </Link>{" "}
      / <span className="text-gray-900 font-medium">{platformName}</span>
    </p>
  </div>
)

const IntegrationHero = ({ data }: { data: IntegrationData }) => {
  const { platform, heroTitle, heroDescription } = data

  return (
    <section
      className={`py-12 md:py-16 relative overflow-hidden ${gradientLightPink}`}
    >
      <div className="pm-container relative z-10">
        <div className="flex flex-col items-center text-center max-w-4xl mx-auto">
          <div className="flex items-center justify-center mb-6 space-x-4 md:space-x-6">
            <a
              href={platform.websiteUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              <img
                src={platform.logoUrl}
                alt={`${platform.name} logo`}
                className="w-14 h-14 md:w-16 md:h-16 object-contain"
              />
            </a>
            <div className="text-2xl md:text-3xl text-gray-400 font-light">
              +
            </div>
            <a
              href="https://premagic.com/"
              target="_blank"
              rel="noopener noreferrer"
            >
              <img
                src={PremagicIcon}
                alt="Premagic logo"
                className="w-14 h-14 md:w-16 md:h-16 object-contain"
              />
            </a>
          </div>

          {platform.tags && platform.tags.length > 0 && (
            <span className="inline-block px-3 py-1 rounded-full bg-blue-100 text-blue-800 text-xs font-medium mb-4">
              {platform.tags[0]}
            </span>
          )}

          <h1 className="text-3xl md:text-5xl font-bold text-[#1F0F61] mb-4 leading-tight">
            {heroTitle.replace("{Platform Name}", platform.name)}
          </h1>

          <p className="text-base md:text-lg text-gray-600 mb-8 max-w-2xl leading-relaxed">
            {heroDescription}
          </p>

          <Link to="/request-a-demo/" className={`${btn} ${btnPrimary}`}>
            Enable This Integration
          </Link>
        </div>
      </div>
    </section>
  )
}

const IntegrationChallenge = ({ data }: { data: IntegrationData }) => {
  return (
    <section className="py-12 md:py-16 bg-white">
      <div className="pm-container">
        <div className="text-center max-w-3xl mx-auto mb-10">
          <h2 className="text-2xl md:text-3xl font-bold text-[#1F0F61] mb-4">
            {data.challengeTitle}
          </h2>
          <p className="text-gray-600">{data.challengeDescription}</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {data.challengePoints.map((point, index) => (
            <div
              key={index}
              className="bg-gray-50 rounded-xl p-5 border border-gray-100"
            >
              <h3 className="text-base font-bold text-[#1F0F61] mb-2">
                {point.title}
              </h3>
              <p className="text-sm text-gray-600 leading-relaxed">
                {point.description}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}

const IntegrationCapabilities = ({ data }: { data: IntegrationData }) => {
  return (
    <section className="py-12 md:py-16 bg-gray-50">
      <div className="pm-container">
        <h2 className="text-2xl md:text-3xl font-bold text-[#1F0F61] mb-8 text-center">
          What You Can Do
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {data.capabilities.map((cap, index) => (
            <div
              key={index}
              className="bg-white rounded-xl p-5 border border-gray-100"
            >
              <div className="flex items-start gap-3 mb-3">
                <div className="flex-shrink-0 w-7 h-7 rounded-full bg-[#2b4bc3] text-white flex items-center justify-center font-semibold text-sm">
                  {index + 1}
                </div>
                <h3 className="text-base font-bold text-[#1F0F61] leading-snug">
                  {cap.title}
                </h3>
              </div>
              <p className="text-sm text-gray-600 mb-3 leading-relaxed">
                {cap.description}
              </p>
              <div className="flex flex-wrap gap-1.5">
                {cap.features.slice(0, 3).map((feature, fIndex) => (
                  <span
                    key={fIndex}
                    className="px-2 py-0.5 bg-gray-100 text-gray-600 text-xs rounded"
                  >
                    {feature}
                  </span>
                ))}
                {cap.features.length > 3 && (
                  <span className="px-2 py-0.5 text-gray-400 text-xs">
                    +{cap.features.length - 3} more
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}

const IntegrationHowItWorks = ({ data }: { data: IntegrationData }) => {
  const steps = data.howItWorksSteps.slice(0, 6)

  return (
    <section className="py-12 md:py-16 bg-white">
      <div className="pm-container">
        <div className="text-center mb-8">
          <h2 className="text-2xl md:text-3xl font-bold text-[#1F0F61]">
            How It Works
          </h2>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center max-w-6xl mx-auto">
          <div className="space-y-4">
            {steps.map((step, index) => (
              <div key={index} className="flex items-start gap-4">
                <div className="flex-shrink-0 w-9 h-9 rounded-full bg-[#2b4bc3] text-white flex items-center justify-center font-bold text-sm">
                  {step.number}
                </div>
                <div>
                  <h3 className="text-base font-bold text-[#1F0F61]">
                    {step.title}
                  </h3>
                  <p className="text-sm text-gray-600">{step.description}</p>
                </div>
              </div>
            ))}
          </div>

          <div>
            <img
              src={FlywheelImage}
              alt="Referral flywheel: Register, Sync, Personalize, Share, Discover, Convert"
              className="w-full h-auto"
            />
          </div>
        </div>
      </div>
    </section>
  )
}

const IntegrationGuideCallout = ({ data }: { data: IntegrationData }) => (
  <section className="py-12 md:py-16 bg-white border-t border-gray-100">
    <div className="pm-container">
      <div className="max-w-4xl mx-auto rounded-2xl border border-purple-100 bg-gradient-to-br from-purple-50/60 via-white to-pink-50/60 p-8 md:p-10">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[#7253F5] mb-2">
              New to Premagic integrations?
            </p>
            <h3 className="text-xl md:text-2xl font-bold text-[#1F0F61] mb-2">
              Start with the integration guide
            </h3>
            <p className="text-gray-600 leading-relaxed max-w-2xl">
              A plain-English overview of what gets synced from {data.platform.name},
              what your attendees see, and what you can embed where. Read it
              before you wire things up.
            </p>
          </div>
          <Link
            to="/integrations/how-it-works/"
            className="inline-flex items-center gap-2 self-start md:self-auto whitespace-nowrap text-base font-semibold text-[#2b4bc3] hover:text-[#1F0F61] transition-colors"
          >
            Read the guide
            <span aria-hidden>→</span>
          </Link>
        </div>
      </div>
    </div>
  </section>
)

const IntegrationBottomCTA = ({ data }: { data: IntegrationData }) => {
  return (
    <section className="py-16 md:py-20 bg-[#1F0F61] text-white text-center">
      <div className="pm-container">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-2xl md:text-4xl font-bold mb-4 text-white">
            {data.ctaTitle}
          </h2>
          <p className="text-base md:text-lg text-blue-100 mb-8 leading-relaxed">
            {data.ctaDescription}
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <Link
              to="/request-a-demo/"
              className={`${btn} bg-white text-[#1F0F61] hover:bg-gray-100`}
            >
              Enable {data.platform.name} Integration
            </Link>
            <Link
              to="/integrations/"
              className={`${btn} border border-white text-white hover:bg-white/10`}
            >
              View All Integrations
            </Link>
          </div>
        </div>
      </div>
    </section>
  )
}

const IntegrationDetail: React.FC<IntegrationDetailProps> = ({ data }) => {
  if (!data) return null

  return (
    <div className="integration-detail-page">
      <IntegrationBreadcrumb platformName={data.platform.name} />
      <IntegrationHero data={data} />
      <IntegrationChallenge data={data} />
      <IntegrationCapabilities data={data} />
      <IntegrationHowItWorks data={data} />
      <IntegrationGuideCallout data={data} />
      <IntegrationBottomCTA data={data} />
    </div>
  )
}

export default IntegrationDetail
